import { createAttachment } from '@vindr/services/apis/attachment.api';
import { getExtensionFileName } from '@vindr/utils/converter';
import { stopReload } from '@vindr/utils/helpers/common.helper';
import { Form } from 'antd';
import { ModalStaticFunctions } from 'antd/es/modal/confirm';
import classNames from 'classnames';
import { enableMapSet } from 'immer';
import { useCallback, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useImmerReducer } from 'use-immer';
import { useEncoder } from '../../common/Capture-v2/Webcam/helper/ConvertEncoder';
import UploadExpand from './UploadExpand';
import UploadMinimize from './UploadMinimize';
import { handleUpload } from './context/UploadAction';
import { initialState, reducer } from './context/reducer';
import { Appearance, UploaderAction } from './context/type';
import { useWakeLock } from './hooks/useWakeLock';
import { cleanDiacritics, uuidv4 } from '@vindr/utils/helpers';

type DicomMode = 'dicom' | 'attachment' | 'pathology' | 'patient';

enableMapSet();

const Upload = ({
  open,
  onCancel,
  acceptType,
  appearance = 'dicom',
  params,
  modal,
  message,
}: {
  open: boolean;
  onCancel: () => void;
  acceptType?: string;
  appearance?: Appearance;
  params?: Parameters<typeof createAttachment>['0'];
  modal: Omit<ModalStaticFunctions, 'warn'>;
  message: any;
}) => {
  const [state, dispatch] = useImmerReducer(reducer, initialState);
  const [form] = Form.useForm();
  const { t } = useTranslation('Vindoc');
  const wakeLock = useWakeLock();
  const encoder = useEncoder(appearance === 'attachment');

  const processingListLength = useMemo(
    () => state.processingList.size,
    [state.processingList.size]
  );
  const failItemLength = useMemo(() => state.failItem.length, [state.failItem]);

  useEffect(() => {
    if (processingListLength === 0 && processingListLength !== state.remainingCount) return;

    if (
      ['success', 'exception'].includes(
        Array.from(state.processingList.values())[processingListLength - 1]?.status
      )
    ) {
      dispatch({ type: 'isFinish', payload: true });
      dispatch({ type: 'isUploading', payload: false });

      window.removeEventListener('beforeunload', stopReload, { capture: true });
      wakeLock.release();
    }
  }, [state.remainingCount, failItemLength, processingListLength]);

  const handleClose = useCallback(() => {
    function actionClose() {
      window.removeEventListener('beforeunload', stopReload, { capture: true });
      wakeLock.release();
      onCancel();
    }

    if (!state.uploading) {
      actionClose();
      return;
    }

    modal.confirm({
      className: 'delete-modal',
      title: t('Are you sure you want to close?'),
      content: t('All files uploading will be canceled.'),
      okText: t('Yes'),
      cancelText: t('No'),
      onOk: () => {
        state.abort.forEach((abortFunc: () => void, index: number) => {
          abortFunc();
          if (index === state.abort.length - 1) {
            actionClose();
          }
        });
      },
    });
  }, [state.uploading, state.abort, modal, t, onCancel]);

  const convertingFiles = useCallback(
    async (files: typeof state['fileList']) => {
      const fileNeedConvert = files.filter((file) => getExtensionFileName(file.name) === 'webm');
      const result = [...files];

      for (const file of fileNeedConvert) {
        const converted = await encoder.convert({
          file: file.originFileObj,
          name: file.name,
          id: file?.fileId,
        });

        const newFile: UploaderAction['payload'] = {
          fileId: file?.fileId,
          name: converted?.outputFileName,
          originFileObj: converted?.renderFile,
          progress: 0,
          status: 'ready',
          size: converted?.renderFile?.size,
        };

        dispatch({
          type: 'editFile',
          payload: newFile,
        });

        const index = result.findIndex((x) => x.fileId === file.fileId);

        result[index] = newFile;
      }

      return result;
    },
    [dispatch, encoder]
  );

  useEffect(() => {
    if (!encoder?.loaded || !encoder?.info) return;
    dispatch({
      type: 'setProgress',
      payload: {
        ids: [encoder?.info?.id],
        progress: encoder?.info?.progress,
        status: 'converting',
      },
    });
  }, [dispatch, encoder?.info, encoder?.loaded]);

  const onUpload = async (dicomMode: DicomMode = 'dicom') => {
    await wakeLock.request();
    window.addEventListener('beforeunload', stopReload, { capture: true });
    switch (appearance) {
      case 'dicom':
        return form.submit();
      case 'attachment': {
        const editParams = { ...params, ...form.getFieldsValue() };

        let convertFiles = await convertingFiles(state.fileList);
        convertFiles = convertFiles.map((it) => ({
          ...it,
          name: `${uuidv4()}_${cleanDiacritics(it.name).replaceAll(' ', '_')}`,
        }));

        form.validateFields().then(async () => {
          const success = await handleUpload({
            dispatch,
            fileList: convertFiles,
            type: 'attachment',
            // Change modality to SM if upload pathology
            params: dicomMode === 'pathology' ? { ...editParams, modality: 'SM' } : editParams,
          });
          if (!success) {
            message.error('Can not upload, please retry or contact administrator for support.');
            dispatch({ type: 'isUploading', payload: false });
          }
        });

        break;
      }
      default:
        return;
    }
  };

  return createPortal(
    <article
      className={classNames(
        'fixed z-50 flex flex-col overflow-y-auto overflow-x-hidden p-4',
        state.isMinimize
          ? 'bottom-0 right-0 w-[600px] max-w-[100vw]'
          : 'h-modal left-0 right-0 top-0 h-full w-full bg-overlay md:inset-0 md:h-full'
      )}
      hidden={!open}
    >
      <UploadMinimize
        dispatch={dispatch}
        failItem={state.failItem}
        fileList={state.fileList}
        handleClose={handleClose}
        isFinish={state.isFinish}
        isReady={state.isReady}
        remainingCount={state.remainingCount}
        show={state.isMinimize}
        uploading={state.uploading}
      />
      <UploadExpand
        appearance={appearance}
        acceptType={acceptType}
        dispatch={dispatch}
        failItem={state.failItem}
        fileList={state.fileList}
        form={form}
        handleClose={handleClose}
        isFinish={state.isFinish}
        isMinimize={state.isMinimize}
        onUpload={onUpload}
        processingList={state.processingList}
        remainingCount={state.remainingCount}
        onCancel={onCancel}
        uploading={state.uploading}
        params={params}
        message={message}
      />
    </article>,
    document.body
  );
};

export default Upload;
